import { EditorDataModel } from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model';
import { IEditorView, IMeasureSizeProps, ISelection, SearchInfo, IPoint } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-common.interface';
import { InteractionEvent } from '@tencent/alloy-renderer';
import { ISelectionParagraph, IParagraph } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { Selection, TextElement, PointPosition, Editor, ParagraphElement, DocumentElement } from '@tencent/alloy-editor';
import {
    IEditorServices,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/interface/editor-service.interface';
import { IEditorProps } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-props.interface';
import { EditorAutoSave } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-controller/editor-auto-save';
import { setLangForParagraphList, mergeTextBodyStyle, processSavedStyles, getParaTextWithEditorValue, getParaTextWithParagraphList, convertPointToParagraphOffset } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils/text-util';
import ResizeMeasure from '@src/slide/domain/ui-model/element-modeles/property/text/editor-measure/resize-measure';
import { AutoFitType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { getDefaultRect, isParaEmpty } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import { OPERATION_TYPE } from '@src/js/constant';
import { IBodyStyle, IParagraphStyle, ITextBodyStyle, ITextRunStyle, IWrapperStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import { deepCopy } from '@src/slide/infrastructure/module/independent/object-operation';
import { getParagraphListBySelection } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils/selection';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { getTouchOrMouseInfo } from '@src/slide/infrastructure/module/independent/mobile/ipad-compatible';
import { EditorEvent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-event';
import { hasBullet } from '@editor/src/page/index/components/editor/data/character';
import { isLatinLang, isSameLang, setLangFromText } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/language';
import { transformParagraphForCharacter } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils';
import DataTransform from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model/data-transform';
import { IpadExternalKeyEventHandler } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils/ipad-keyboard';
import { ESpecialOperationType } from '@src/slide/application/service/selection/selected-with-commit-manager.interface';
import { isVerticalDirection } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-direction';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { EditorOperations } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-controller/editor-operations/operations';
import { convertKeyCodeWithTextDirection } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils/text-direction';

const MOUSE_RIGHT_BUTTON = 2;
/**
 * 主要处理查看态和编辑态editor的业务逻辑。数据更新等。
 */
export class EditorController {
    private editorView: IEditorView;
    private dataModel: EditorDataModel;

    private services: IEditorServices;

    private hasValueChange = false;  // 数据是否发生变更
    private saveFocusCallBack: (() => void)|null|undefined;
    private alreadyChange = false;  // 通过editor修改数据，hasValueChange回调会异步返回，这里记录一下这个状态是否已经同步去修改了sdk数据
    /**
     * 文本自动保存逻辑
     */
    private editorAutoSave: EditorAutoSave;
    private operationKey: string;
    private editorEvent: EditorEvent;
    private operationHandler: EditorOperations;

    /**
     * 构造函数
     * @param editorView View层
     * @param dataModel Model数据层
     * @param services
     */
    public constructor(editorView: IEditorView, dataModel: EditorDataModel, services: IEditorServices) {
        this.editorView = editorView;
        this.dataModel = dataModel;
        this.services = services;
        this.operationHandler = this.createOperations();
        this.editorAutoSave = new EditorAutoSave({
            triggerUploadData: () => {
                if (!this.editor.isComposing) this.triggerSave();
            },
            resetHasValueChangeFlag: () => {
                this.updateHasValueChangeFlag(false);
            },
        });
    }

    public get operationRange() {
        return this.isEditing ? this.editor.selection : this.editor.getRange();
    }

    public get isEditing(): boolean {
        return this.editor?.isEditing || false;
    }

    public get isReadyEditing(): boolean {
        return this.editor?.isReadyEditing || false;
    }

    public get isTextEnterable(): boolean {
        return this.editor?.isTextEnterable || false;
    }

    public get changeFlag(): boolean {
        return this.hasValueChange || false;
    }

    public setFocus(callBack?: () => void, event?: InteractionEvent) {
        if (this.isEditing) {
            this.editor?.focus();
            callBack?.();
        } else {
            this.saveFocusCallBack = callBack;
            if (event && typeof event.worldX === 'number' && typeof event.worldY === 'number') {
                // 使光标显示在点击位置附近
                this.editor?.triggerMouseDown({
                    posXOfWorld: event.worldX,
                    posYOfWorld: event.worldY,
                });
            } else {
                this.editor?.focus();
            }
        }
    }

    public setReadyEditing(): void {
        this.editor?.setReadyEditing();
    }

    public triggerBlur() {
        if (this.isReadyEditing) {
            this.removeReadyEditing();
            return;
        }
        this.editor?.blur();
        if (!this.isEditing) {
            this.onBlur?.();
        }
    }

    public removeReadyEditing(): void {
        this.editor?.removeReadyEditing();
    }

    public operations(key: string, value, option): boolean {
        this.operationKey = key;
        this.hasValueChange && this.triggerUpdate();  // 修改之前，提前保存一次。这样撤销时，只会撤销这一次操作
        const { isHandled, needUpdate } = this.operationHandler.operations(key, value, option);
        if (needUpdate) {
            // 在有选区的时候标记 alreadyChange 为 true，因为有选区时后续还会触发一次 handleValueChange 回调，加这个标志主要是为了避免重复保存
            // 光标态时设置文字相关样式不会触发 handleValueChange（因为数据并没有改变），如果设置了 alreadyChange 为 true 的话，
            //   会导致下次有效的 handleValueChange 被忽略， 导致数据可能不会保存
            if (this.isExpanded()) {
                this.alreadyChange = true;
            }
            // 效果预览中，更新的效果值无需上传(不能放在triggerUpdate里，因为在先输入文字再选择效果的场景下，需要上面的保存一次，否则会被font-refresh清除)
            if (!this.isPreview?.()) {
                this.triggerUpdate();
            }
        }
        this.props.onStyleChange?.({
            style: this.editorView.getAllStylesSync(),
        });
        return isHandled;
    }

    public getBackDeleteTextProps(): string[] {
        if (this.isEditing) {
            const styles = this.editor.getStyle();
            if ((styles as any)?.inlineProps) {
                return [];
            }
            return ['origin-href'];
        }
        return [];
    }

    /**
     * 添加查找替换当前高亮文本信息
     * @param result
     * @param keyword
     */
    public addHighlight(result, keyword: string): void {
        const searchInfo: SearchInfo = { isHighlightFlag: true, highlightResult: result, keyword };
        this.dataModel.setRawData({ searchInfo }, true);
    }

    /**
     * 添加查找到的文本信息
     * @param result
     * @param keyword
     */
    public addSearchMark(result, keyword: string): void {
        const searchInfo: SearchInfo = { isSearchingFlag: true, searchResult: result, keyword };
        this.dataModel.setRawData({ searchInfo }, true);
    }

    /**
     * 移除查找信息
     */
    public removeHighlight(): void {
        const searchInfo: SearchInfo = { isHighlightFlag: false, highlightResult: [], keyword: '' };
        this.dataModel.setRawData({ searchInfo }, true);
    }

    /**
     * 移除高亮信息
     */
    public removeSearchMark(): void {
        const searchInfo: SearchInfo = { isSearchingFlag: false, searchResult: [], keyword: '' };
        this.dataModel.setRawData({ searchInfo }, true);
    }

    public copy() {
        if (!this.isExpanded()) return null;
        const paragraphs = this.editor.copy();
        if (!paragraphs) {
            return null;
        }
        return {
            paragraphListWithoutInherit: {
                paragraphList: EditorDataModel.convertParagraphElementsToParagraphList(
                    paragraphs as ParagraphElement[],
                    this.dataModel.resizable,
                ),
            },
            paragraphListWithInherit: {
                paragraphList: EditorDataModel.convertParagraphElementsToParagraphList(
                    paragraphs as ParagraphElement[],
                    this.dataModel.resizable,
                    false, false,
                ),
            },
        };
    }

    public paste(textBody, keepProps = true) {
        const onlyPaste = () => {
            // 触发粘贴之前触发保存，否则在粘贴后选择其他选择性粘贴逻辑会导致原文本修改丢失
            this.triggerSave();
            const paragraphs = this.dataModel.doConvertParagraphListToParagraphElements(textBody.paragraphList);
            this.props.onBeforeValueChange?.();
            this.editor.paste(paragraphs, keepProps);
            this.triggerUpdate();
            this.alreadyChange = true;
        };
        if (this.isEditing) {
            return onlyPaste();
        }
        // 如果不在编辑态，先进入编辑态再执行粘贴
        this.setFocus(() => {
            onlyPaste();
        });
    }

    public cut() {
        if (this.isEditing) {
            if (!this.isExpanded()) return null;
            this.props.onBeforeValueChange?.();
            const paragraphs = this.editor.cut();
            this.triggerUpdate();
            return {
                paragraphListWithoutInherit: {
                    paragraphList: EditorDataModel.convertParagraphElementsToParagraphList(
                        paragraphs as ParagraphElement[],
                        this.dataModel.resizable,
                    ),
                },
                paragraphListWithInherit: {
                    paragraphList: EditorDataModel.convertParagraphElementsToParagraphList(
                        paragraphs as ParagraphElement[],
                        this.dataModel.resizable,
                        false, false,
                    ),
                },
            };
        }
        return null;
    }

    public emptyParagraph(): void {
        const paragraphList = this.dataModel.getParagraphList(true);
        if (!paragraphList?.length) return;

        const p = paragraphList.slice(0, 1);
        const t = p[0].textRuns;
        p[0].textRuns = (t?.length && t.slice(0, 1)) || [{
            text: '',
            styles: {},
        }];
        p[0].textRuns[0].text = '';

        this.updateRawData(p);
    }

    public getExpandedRect() {
        if (this.isEditing) {
            return this.editor.getSelectionRect?.() || null;
        }
        return null;
    }

    /**
     * 获取editor当前选区，并提供转换offset的能力。
     * 需要转换offset的原因：新版editor中 Point的offset是在对应textRun的偏移，老版本是对应paragraph整体的偏移
     * @param needConvertOffset
     */
    public getSelection(needConvertOffset = true): ISelection {
        const editorSelection = this.editor?.selection;

        if (editorSelection && needConvertOffset) {
            return this.editor!.rangeUtil.convertToParagraph(editorSelection);
        }

        return editorSelection as ISelection;
    }

    public getSelectionPara(): Array<ISelectionParagraph> {
        if (this.isEditing) {
            const editorSelection = this.getSelection();
            const paragraphList = this.dataModel.getParagraphList();

            return getParagraphListBySelection(editorSelection, paragraphList.map((para: IParagraph) => ({
                ...para,
                paragraphProps: {
                // 这里老版本数据有打平的paragraphCharacter属性，新版本的数据上没有
                // 因为后续还是用到了一些DataManger的方法从这里取character属性，所以手动打平一下
                    ...para.paragraphProps,
                    ...para.paragraphCharacter,
                },
            })));
        }

        return [];
    }

    public getTextLinkState(): boolean {
        return !!this.getLinkManager()?.getTextLinkState(this.editor);
    }

    public setSelectionAll(): void {
        this.editor?.selectAll?.();
    }

    public setTextLink(): boolean {
        if (this.isEditing) {
            this.getLinkManager()?.getLinkDataAndSetLink({
                editor: this.editor, updateDataCB: () => this.triggerUpdate() });
            return true;
        }
        return false;
    }

    public insertText(text, styles): void {
        this.editor.insertText(text, processSavedStyles(styles));
    }

    public insertTextWithNoAutoSave(text, styles): void {
        this.editor.insertText(text, processSavedStyles(styles));
        this.editorAutoSave.cancel();
    }

    public insertTextWithSaveNow(text, styles): void {
        this.editor.insertText(text, processSavedStyles(styles));
        this.triggerUpdate();
    }

    public insertTextWithSaveNowAndExitEditing(text, styles): void {
        this.insertTextWithSaveNow(text, styles);
        this.triggerBlur();
        this.exitEditing();
    }

    public triggerRedo(): void {
        if (this.isReadOnly) return;
        if (browser.isMobile) {
            this.triggerBlur();
        } else {
            this.hasValueChange && this.triggerUpdate();
        }
        this.handleRedo?.();
    }

    public triggerUndo(): void {
        if (this.isReadOnly) return;
        if (browser.isMobile) {
            this.triggerBlur();
        } else {
            this.hasValueChange && this.triggerUpdate();
        }
        this.handleUndo?.();
    }

    public handleMouseMove(e: MouseEvent): void {
        if (this.isEditing) {
            this.onMouseMove?.();
        }
        const { x, y } = this.editorView.getAppPosition();
        this.editor?.triggerMouseMove({
            posXOfWorld: e.pageX - x,
            posYOfWorld: e.pageY - y,
        });
    }

    public handleValueChange(): void {
        if (!this.alreadyChange) {
            this.updateHasValueChangeFlag(true);
        }
        this.alreadyChange = false;
        const paraText = getParaTextWithEditorValue(this.editor.getValue());
        this.editorAutoSave.onEditorChange(paraText);
        this.handleResizeWhenTextChange();
        const { width, height } = this.editor.getLayoutSize();
        // 尺寸向上取整，避免更新尺寸之后和当前排版结果不一致
        this.props.onTextChange?.({ width: Math.ceil(width), height: Math.ceil(height) });
        this.getLinkManager()?.linkFloatBarService.hideAndClearTimeout();
    }

    public handleCompositionInput(): void {
        this.updateHasValueChangeFlag(true);
        const { width, height } = this.editor.getLayoutSize();
        // 尺寸向上取整，避免更新尺寸之后和当前排版结果不一致
        this.props.onTextChange?.({ width: Math.ceil(width), height: Math.ceil(height) });
    }

    public handleMouseDown(e: InteractionEvent): void {
        if (!this.props.noStopMouseDown) {
            e.stopPropagation();  // 除非外部需要放开冒泡，否则默认阻止mouseDown冒泡。
        }

        // 右键不触发trigger
        const event: MouseEvent = e.nativeEvent as MouseEvent;
        if (!(event?.button === MOUSE_RIGHT_BUTTON && this.isExpanded())) {
            this.editor?.triggerMouseDown({
                posXOfWorld: e.worldX,
                posYOfWorld: e.worldY,
            }, event);
        }

        this.onMouseDown(event);  // 如果查看态和编辑态都会响应mouseDown，建议props回调方到lite里
    }

    public handleMouseUp(): void {
        this.onMouseUp();
        this.editor.triggerMouseUp();
    }

    public handleClick(e?: InteractionEvent) {
        this.props.onClick?.(e?.nativeEvent);
    }

    public handleTouchStart(e: InteractionEvent): void {
        if (this.isEditing) {
            // 在编辑态才触发内部单击双击三击逻辑
            this.editor?.triggerTouchStart({ posXOfWorld: e.worldX, posYOfWorld: e.worldY });
            const event: MouseEvent = e.nativeEvent as MouseEvent;
            this.props.onTouchStart?.(event);
        } else {
            this.editor?.triggerTouchStart({ posXOfWorld: e.worldX, posYOfWorld: e.worldY });
        }
    }

    public handleTouchMove(e: TouchEvent): void {
        if (this.isEditing) {
            // 根据当前鼠标位置和画布的位置计算出当前鼠标相对于画布的位置
            const { x, y } = this.editorView.getAppPosition();
            // 需要考虑到页面滚动的距离
            const { scrollTop = 0, scrollLeft = 0 } = document.documentElement;
            this.editor.triggerTouchMove({
                posXOfWorld: getTouchOrMouseInfo(e).pageX - (x + scrollLeft),
                posYOfWorld: getTouchOrMouseInfo(e).pageY - (y + scrollTop),
            });
        }
    }

    public handleTouchEnd(position: PointPosition): void {
        this.editor?.triggerTouchEnd(position);
    }

    public updateEditorData() {
        const documentElement = this.editor?.getValue();  // 实时获取UI数据更新
        if (documentElement) {
            // eslint-disable-next-line max-len
            const { paragraphList, textBodyStyle } = this.dataModel.doConvertFromDocElementKeepInheritData(documentElement);
            const { wrapperStyle, bodyStyle, autoFitType } = textBodyStyle || {};
            this.updateRawData(paragraphList, wrapperStyle, bodyStyle, autoFitType, true);
        }
    }

    public handleKeyDown(e: KeyboardEvent): boolean {
        // readyEditing 状态中也会触发 keydown，此时不需要响应内部 keydown 逻辑
        if (this.isEditing) {
            this.onKeyDown(e);
            const titlePlFromML = this.dataModel.model?.isTitleOrCtrTitlePl
            && (this.dataModel.model?.isFromMaster
            || this.dataModel.model?.isFromLayout);
            if (e.key === 'Enter' && titlePlFromML) {
                this.editor.insertSoftBreak();
                return true;
            }
            if (e.key === 'Enter' || e.key === ' ') {
                this.getLinkManager()?.setLinkWhenEnter(this.editor, () => {
                // 当前是 keydown 事件，早于 input 事件触发，所以如果此时直接触发保存，保存的文字中不包括当前的回车或空格
                // 在即将触发的 input 事件中会触发新的定时保存，从而导致 editor 的数据发生变化
                // 如果在触发定时保存前鼠标 hover 到链接上，此时从当前文字节点拿到的 textElement 在自动保存之后已经变化，所以在进行链接操作可能就会出现问题
                    requestAnimationFrame(() => {
                        this.triggerSave();
                    });
                });
            }
            if (!this.editorEvent) {
                this.editorEvent = new EditorEvent({
                    editor: this.editor,
                    getSelection: this.getSelection.bind(this),
                    operationHandler: this.operationHandler,
                    shouldPreventTab: this.props.shouldPreventTab ?? false,
                    triggerUndo: this.triggerUndo.bind(this),
                    triggerRedo: this.triggerRedo.bind(this),
                });
            }
            return this.editorEvent.handleEvent(e);
        }
        return false;
    }

    /**
     * 选区或光标发生变更
     */
    public handleSelectionChange(prevSelection: Selection, currentSelection: Selection): void {
        if (this.isEditing) {
            this.handleParagraphCharacter(prevSelection, currentSelection);
            this.props.onStyleChange?.({
                style: this.editorView.getAllStylesSync(),
            });
            this.handleSelectionChangeInEditing?.(currentSelection);
        } else {
            this.handleParagraphCharacter(prevSelection, currentSelection);
        }
    }

    public handleBeforeInput(text: string, textElement: TextElement): Partial<TextElement> | void {
        if (this.isEditing || this.isReadyEditing) {
            const focus = this.editor.selection?.focus as IPoint;
            const preText = textElement.text[focus.offset - 1] || '';
            // 当前字符与前一个字符中西文一样
            // 或者当前字符与前一个字符都是中文（合成输入过程中修改成西文字体，输入确定后需要变更回中文字体）
            if (!isSameLang(preText, text) || (!isLatinLang(preText) && !isLatinLang(text))) {
                const langStyle: { lang?: string } = {} ;
                setLangFromText(text, langStyle);
                const { fonts,  fontFamily } = DataTransform.getOriginProps(textElement, 'raw-') as ITextRunStyle;
                const newfontFamily = fonts?.[langStyle.lang ?? ''] || fontFamily;
                return {
                    fontFamily: DataTransform.transformTextStyle('fontFamily', newfontFamily)?.fontFamily, // canvas中文字体需要转换下
                    'origin-fontFamily': newfontFamily, // toolbar字体回显
                    'origin-lang': langStyle.lang,
                    'origin-raw-lang': true,
                };
            }
        }
    }

    public onFocus(): void {
        if (this.dataModel.noSaveRawData) {
            // 如果发现没有存储rawData，在进入编辑态之后，要更新rawData
            this.dataModel.setRawData({ noSaveRawData: false }, true);
        }
    }

    public enterEditing(): void {
        // 在这里调用setFocus的callback
        if (this.saveFocusCallBack) {
            this.saveFocusCallBack();
            this.clearFocusCallBack();
        }

        // 注册 ipad 额外键盘事件
        IpadExternalKeyEventHandler.register();
        // 获取焦点，自动保存逻辑进入初始态
        this.editorAutoSave.enterInputIdle(getParaTextWithParagraphList(this.dataModel.getParagraphList()));

        if (this.dataModel.noSaveRawData) {
            // 如果发现没有存储rawData，在进入编辑态之后，要更新rawData
            this.dataModel.setRawData({ noSaveRawData: false }, true);
        }
        this.updateHasValueChangeFlag(false);  // 进入焦点，重置状态
        // 进入编辑态，更新style样式
        this.props.onStyleChange?.({
            style: this.editorView.getAllStylesSync(),
        });
        this.props.onEnterEditing?.();
    }

    public exitEditing(): void {
        this.clearFocusCallBack();

        // 失去焦点触发自动保存
        if (this.hasValueChange) {
            const { model } = this.props;
            if (model?.selectionService) {
                // 因为失焦时选中态已经没了，为了保证undo时能选中文本框，所以将选择态手动记录一下
                const beforeSelectedSnapshot: any = {
                    currentSlideType: model.selectionService.currentSlideType,
                    slideIdArr: [model.selectionService.currentSlide.id],
                };
                if (model.rootGroupModel) {
                    beforeSelectedSnapshot.groupElementIdArr = [{ rootId: model.rootGroupModel?.id || '', childId: model.id || '' }];
                    beforeSelectedSnapshot.slideElementIdArr = [model.rootGroupModel?.id];
                } else if (model.inTable) {
                    beforeSelectedSnapshot.groupElementIdArr = [];
                    beforeSelectedSnapshot.slideElementIdArr = [model.tableModelRef.id];
                } else {
                    beforeSelectedSnapshot.groupElementIdArr = [];
                    beforeSelectedSnapshot.slideElementIdArr = [model.id];
                }
                model.selectionService.selectedWithCommit
                    .markSpecialOperate(ESpecialOperationType.textBlur, { beforeSelectedSnapshot });
            }
            this.triggerUpdate();
        }
        // 执行一些清理操作
        this.clear();
        IpadExternalKeyEventHandler.unRegister();

        this.props.onStyleChange?.({
            style: this.editorView.getAllStylesSync(),
        });
        this.props.onExitEditing?.();
    }

    public clearFocusCallBack(): void {
        this.saveFocusCallBack = null;
    }

    public setSelectionToEnd(): void {
        if (this.isEditing) {
            this.editor.selectEnd();
        }
    }

    public triggerSave(): void {
        if (this.isEditing && this.hasValueChange) {
            this.triggerUpdate();
        }
    }
    /**
     * 重置「文本是否有改变」的标记，在外界触发文字保存之后执行，避免重复保存
     */
    public resetValueChangeFlag() {
        if (this.isEditing) {
            // 在有选区的时候标记 alreadyChange 为 true，因为有选区时后续还会触发一次 handleValueChange 回调
            if (this.isExpanded()) {
                this.alreadyChange = true;
            }
            this.updateHasValueChangeFlag(false);
        }
    }

    public dispose(): void {
        // 执行一些销毁操作
        this.clear();
    }

    public clear(): void {
        this.updateHasValueChangeFlag(false);
        this.alreadyChange = false;

        this.editorAutoSave.cancel();  // 如果有正在输入的状态，cancel掉
        this.editorAutoSave.clear();

        this.getLinkManager()?.linkEditMenuService.hide();
        this.getLinkManager()?.linkFloatBarService.hideAndClearTimeout();
        this.onBlur?.();
    }

    public getParagraphTextAndOffset(selection: Selection) {
        if (!selection) {
            return;
        }
        const { focus } = selection;
        const [, focusParagraphPath] = this.editor.getParent(focus.path);
        // 获取当前段落文字
        const focusParagraphText = this.editor.getString(focusParagraphPath);
        const paragraphList = this.editor?.getValue()?.children;
        // 获取 focus 位置相对于段落的偏移
        const { offset } = convertPointToParagraphOffset(focus as IPoint, paragraphList);
        return { offset, focusParagraphText };
    }

    /**
    * 文本格式为自缩排，全选文字设置字体大小，如果新的文本高度超过元素高度，将元素格式改为不自动调整
    * @param paragraphList
    * @param autoFitType
    * @returns {string|*}
    */
    public getAutoFitType = (paragraphList, autoFitType) => {
        if (autoFitType !== AutoFitType.FONT_AUTO_FIT
            || !this.isSelectAllText()
            || (this.operationKey !== OPERATION_TYPE.FONT_SIZE && this.operationKey !== OPERATION_TYPE.FONT_SIZE_UP)) {
            return autoFitType;
        }

        const textRect = { ...(this.props.textRect || getDefaultRect()) };
        const textBodyStyle = {
            ...this.props.textBodyStyle,
        };
        const documentValue = EditorDataModel.convertFromTextModel(
            { paragraphList, textBodyStyle },
            { noConvertBack: false },
        );

        const height = ResizeMeasure.getEditorBlockSize(documentValue, { textRect, textBodyStyle });
        const isVert = isVerticalDirection(textBodyStyle.wrapperStyle?.textDirection);
        if (height > (isVert ? textRect.width : textRect.height)) {
            autoFitType = AutoFitType.NO_AUTO_FIT;
        }

        return autoFitType;
    };

    public isSelectAllText(): boolean {
        return !this.isEditing || this.editor.isSelectAll?.() || false;
    }

    /**
     * 触发数据更新,触发上传
     * @param paragraphList 更新后的段落新
     * @param wrapperStyle 以下都是更新后的文本框样式
     * @param bodyStyle
     * @param autoFitType
     * @param forceUpdate 强制更新sdk
     */
    // eslint-disable-next-line complexity
    public updateRawData(
        paragraphList: IParagraph[], wrapperStyle?: IWrapperStyle,
        bodyStyle?: IBodyStyle, autoFitType?: string, forceUpdate?: boolean,
    ): void {
        if (this.editorView.onChange) { // 无可编辑权限，直接返回
            // 需要更新的 textBodyStyle
            const updateTextBodyStyle: ITextBodyStyle = {};
            if (wrapperStyle) {
                updateTextBodyStyle.wrapperStyle = wrapperStyle;
            }
            if (autoFitType) {
                updateTextBodyStyle.autoFitType = autoFitType;
            }
            if (bodyStyle) {
                updateTextBodyStyle.bodyStyle = bodyStyle;
            }
            // 获取完整的 textBodyStyle，用于生成 editorValue 和计算 fontScale
            let fullTextBodyStyle = mergeTextBodyStyle(this.dataModel.getTextBodyStyle(), updateTextBodyStyle);

            // 更新字体lang
            setLangForParagraphList(paragraphList);

            // 同步更新到editorModel
            fullTextBodyStyle = mergeTextBodyStyle(fullTextBodyStyle, updateTextBodyStyle);
            // 标志当前数据是临时数据，目的是为了和后续存库后changeset变更引发的setRawData的数据区分开
            // 因为changeset引发的setRawData时会对比paragraphList，如果是相同的则不会做类似onStyleChange的操作
            // 协同没问题，必有变化，但是主动操作时已经在此处把editorModel的paragraphList更新了
            // 从而保存后changeset再次引发setRawData一对比则是一样的了，那就有问题，所以在此处加个临时标识
            if (paragraphList?.[0]) {
                paragraphList[0].isEditorModelTempData = true;
            }
            if (fullTextBodyStyle) {
                fullTextBodyStyle.isEditorModelTempData = true;
            }
            this.dataModel.setRawData({
                paragraphList,
                textBodyStyle: fullTextBodyStyle,
            }, true, true);

            const n = {
                // 数据通过 onchange 抛出去之后理论上可以随意修改，所以深拷贝一份，避免影响内部逻辑
                paragraphList: deepCopy(paragraphList),
                textBodyStyle: updateTextBodyStyle,
            };
            if (this.needInheritStyleShape()) {
                // 需要全量的元素重新走一次转换
                const documentElement = this.dataModel.doConvertFromTextModel(paragraphList);
                const inheritData = this.getConvertFromDocElement(documentElement);
                Object.assign(n, inheritData);
            }

            if (!this.isPreview?.()) {
                // 预览状态 不更新sdk
                this.editorView.onChange(n, this.checkIsEditing() || forceUpdate || false);
            }
        }
    }

    /**
     *  选区变化时，强制刷新项目符号
     * @returns
     */
    // eslint-disable-next-line complexity
    public handleParagraphCharacter(prevSelection: Selection, currentSelection: Selection): void {
        // 只读的不需要动态处理项目符号
        if (this.editorView.isReadOnly()) {
            return;
        }
        const editorValue = this.editor?.getValue() as DocumentElement;
        if (!editorValue) return;

        const documentParagraphList = editorValue.children;
        const isCurrentSelectionExpand = this.isSelectionExpand(currentSelection);
        const isPrevSelectionExpand = this.isSelectionExpand(prevSelection);
        const paragraphList = documentParagraphList.map(transformParagraphForCharacter);
        let needForceRender = false;
        let currentParaIndex;
        let prevParaIndex;
        if (!isCurrentSelectionExpand && currentSelection) {
            [currentParaIndex] = currentSelection.anchor.path;
            const currentParagraph = paragraphList[currentParaIndex];
            const { character } = currentParagraph?.paragraphProps || {};
            if (hasBullet(character) && isParaEmpty(currentParagraph)) {
                // 只有在当前段落为空时才会涉及到项目符号的透明度变化，才需要强制刷新
                needForceRender = true;
            }
        }
        if (!isPrevSelectionExpand && prevSelection) {
            [prevParaIndex] = prevSelection.anchor.path;
            const prevParagraph = paragraphList[prevParaIndex];
            const { character } = prevParagraph?.paragraphProps || {};
            if (hasBullet(character) && isParaEmpty(prevParagraph)) {
                // 只有在之前段落为空时才会涉及到项目符号的透明度变化，才需要强制刷新
                needForceRender = true;
            }
        }
        if (typeof currentParaIndex === 'number' && typeof prevParaIndex === 'number' && currentParaIndex === prevParaIndex) return;
        /**
         * forceRender 放在 requestAnimationFrame 中是因为：
         *  forceRender 之后 TextEditor 这个元素的内容都会清掉重新渲染，可能会影响当前帧的事件触发逻辑
         * 比如：移动端切换光标监听的是 document 捕获阶段的 touchend 事件，在 touchend 中会触发 onSelectionChange，
         *  如果在这个时候触发了 forceRender，在事件传播到 canvas 的时候，原有响应到事件的色块对应的元素已经没了，
         *  所以渲染层元素就无法触发 touchend 事件，导致 touchend 事件被冒泡到了上层，从而取消元素选中
         */
        requestAnimationFrame(() => {
            // forceRender触发editor强制刷新，从而触发beforalayout，触发changeParagraphCharacter
            needForceRender && this.editor?.forceRender();
        });
    }

    public isSelectionExpand(selection: Selection): boolean {
        if (!selection) return false;
        const { anchor, focus } = selection;
        if (anchor.path.toString() === focus.path.toString() && anchor.offset === focus.offset) return false;
        return true;
    }

    /**
     * 设置选中的文本样式
     * @param styles 单个或多个样式
     * @param selection 指定选区，不指定生效当前选区
     */
    public setStylesForSelection = (styles: ITextRunStyle, selection?): void => {
        /* eslint-disable no-param-reassign */
        const textStyles = Object.entries(styles).reduce((newStyles, [key, value]) => {
            const textStyle = DataTransform.transformTextStyle(key, value, styles);
            if (textStyle) {
                const [[propKey, propValue]] = Object.entries(textStyle);
                newStyles[propKey] = propValue;
            }
            newStyles[`origin-${key}`] = value; // 更新原始数据
            newStyles[`origin-raw-${key}`] = true; // 更新继承状态
            return newStyles;
        }, {});

        const { isFromMaster, isFromLayout, isPlaceholder } = this.dataModel.model || {};
        const isMasterLayoutPl = (isFromMaster || isFromLayout) && isPlaceholder;

        // masterLayout 下的占位符要特殊处理
        if (isMasterLayoutPl) {
            this.handleMasterPlaceholder(textStyles, selection);
        } else {
            this.editor.changeTextProps(textStyles, selection, (propKey, oldProps, newProps) => {
                // 效果的effect字段更新是新旧merge而不是直接替换
                if ([ActionType.EFFECT, `origin-${ActionType.EFFECT}`].includes(propKey as string)) {
                    return { ...oldProps, ...newProps };
                }
                return newProps;
            });
        }
    };

    public get props(): IEditorProps {
        return this.editorView.getProps();
    }

    /**
     * 移除选中文本样式
     * @param removeStyles 移除样式
     */
    public removeStylesForSelection = (
        removeStyles: ITextRunStyle | Partial<TextElement>,
        selection?: Selection,
    ): void => {
        Object.entries(removeStyles).forEach(([key, value]) => {
            const textStyle = DataTransform.transformTextStyle(key, value);
            if (textStyle) {
                const [propKey, propValue] = Object.entries(textStyle)[0];
                this.editor.removeTextProps(propKey, selection);
            } else {
                this.editor.removeTextProps(key, selection);
            }
            this.editor.removeTextProps(`origin-${key}`, selection);  // 移除原始数据
        });
    };

    /**
     * 母版编辑的时候，部分占位符逻辑需要获取全量的样式进行更新。
     * 比如 母版上的标题占位符更新文本样式，实际上是更新到 master 的 titleStyleList 里，所以无需区分继承数据
     * 20240604 新增：正常的文本编辑保存时段落也需要包含继承的数据，因为在 3.0 中 ui-sdk 时会根据标识过滤出自身数据然后触发保存
     * @param documentElement
     * @returns
     */
    public getConvertFromDocElement(documentElement: DocumentElement) {
        return this.dataModel.doConvertFromDocElementKeepInheritData(documentElement);
    }

    public getSelectionLeafNodeAndRangeRef() {
        return this.editor.getSelectionLeafNodeAndRangeRef();
    }

    public getLeafNodeAndRangeRef(selection: Selection) {
        return this.editor.getLeafNodeAndRangeRef(selection);
    }

    public isExpanded(): boolean {
        return this.editor.isSelectionExpanded?.() || false;
    }

    public getStyle() {
        return this.editor.getStyle();
    }

    /**
     * 设置文本框属性
     * @param styles 文本框属性
     */
    public setStylesForTextBody = (styles: IWrapperStyle & IBodyStyle): void => {
        /* eslint-disable no-param-reassign */
        this.updateHasValueChangeFlag(true);  // textBody的属性暂时手动触发ValueChange
        const textBodyStyles = Object.entries(styles).reduce((newStyles, [key, value]) => {
            const textBodyStyle = DataTransform.transformEditorStyle(key, value, styles);
            if (textBodyStyle) {
                const [[propKey, propValue]] = Object.entries(textBodyStyle);
                newStyles[propKey] = propValue;
            }
            newStyles[`origin-${key}`] = value; // 更新原始数据
            newStyles[`origin-raw-${key}`] = true; // 更新继承状态
            return newStyles;
        }, {});
        this.editor.changeDocumentProps(textBodyStyles);
    };

    /**
     * 设置段落属性
     * @param styles 段落样式
     */
    public setStylesForPara = (styles: IParagraphStyle, location?): void => {
        /* eslint-disable no-param-reassign */
        const paraStyles = Object.entries(styles).reduce((newStyles, [key, value]) => {
            const paraStyle = DataTransform.transformParaStyle(key, value, styles);
            if (paraStyle) {
                const [[propKey, propValue]] = Object.entries(paraStyle);
                newStyles[propKey] = propValue;
            }
            newStyles[`origin-${key}`] = value; // 更新原始数据
            newStyles[`origin-raw-${key}`] = true; // 更新继承状态
            return newStyles;
        }, {});
        this.editor.changeParagraphProps(paraStyles, location);
    };

    public getEditorValue(): DocumentElement {
        return this.editor.getValue();
    }

    public getSelectionNodes(match: (node) => boolean) {
        return this.editor?.getSelectionNodes(match);
    }

    /**
     * 获取editor UI组件
     */
    private get editor(): Editor {
        return this.editorView.getAlloyEditor();
    }

    /**
     * 修改 hasValueChange 标记都使用这个方法，需要在这里触发外界回调
     * @param hasValueChange
     */
    private updateHasValueChangeFlag = (hasValueChange: boolean) => {
        this.hasValueChange = hasValueChange;
        this.editorView.updateChangeFlag(hasValueChange);
    };

    /**
     * 获取实时UI数据，触发数据上传
     */
    private triggerUpdate() {
        this.editorAutoSave.cancel();
        const documentElement = this.editor.getValue();  // 实时获取UI数据更新
        this.updateData(documentElement);
        this.updateHasValueChangeFlag(false); // 保存数据后，重置状态
    }

    /**
     * 编辑态更新数据通用接口
     */
    private updateData(documentElement: DocumentElement): void {
        const { paragraphList, textBodyStyle } = this.getConvertFromDocElement(documentElement);
        const autoFitType = this.getAutoFitType(paragraphList, this.dataModel.autoFitType);
        // 需要更新的 textBodyStyle，修改字体可能会导致自缩排属性变化
        const updateTextBodyStyle = autoFitType === this.dataModel.autoFitType ? textBodyStyle : {
            ...textBodyStyle,
            autoFitType,
        };
        const savedParagraphList = paragraphList;  // 上面已经拷贝，这里以及无需拷贝一份了
        setLangForParagraphList(savedParagraphList);
        this.editorView.onChange?.({
            paragraphList: savedParagraphList,
            textBodyStyle: updateTextBodyStyle,
        }, true);
    }

    /**
     * 需要全量的样式数据，比如母版上的标题占位符更新文本样式，实际上是更新到 master 的 titleStyleList 里，所以无需区分继承数据
     * @returns
     */
    private needInheritStyleShape() {
        const { isTitleOrCtrTitlePl, isBodyPl, isFromMaster } = this.dataModel.model || {};
        return isFromMaster && (isTitleOrCtrTitlePl || isBodyPl);
    }

    private checkIsEditing(): boolean {
        return this.editor?.isEditing;
    }

    private getLinkManager() {
        return this.services.linkManager;
    }

    private get isReadOnly(): boolean {
        return this.editorView.isReadOnly();
    }

    /**
     * 处理输入时文本自缩排
     */
    private handleResizeWhenTextChange(): void {
        if (this.dataModel.resizable) {
            const editorValue = this.editor.getValue() as DocumentElement;
            const { paragraphList } = this.getConvertFromDocElement(editorValue) as any;
            const { textRect, modelKey, isTitle } = this.props as IMeasureSizeProps;
            const measureProps = {
                textRect,
                modelKey,
                isTitle,
                textBodyStyle: this.dataModel.getTextBodyStyle(),
            };
            const resizeLevel = ResizeMeasure.measureResizeLevel(editorValue, measureProps);
            if (this.dataModel.resizeLevel !== resizeLevel) { //  缩排等级变化了才刷新UI
                this.dataModel.setRawData({
                    paragraphList,
                    resizeLevel,
                }, true, true); // 无需比较，强制更新
            }
        }
    }

    private handleMasterPlaceholder = (textStyles, selection = this.editor.selection): void => {
        const { isTitleOrCtrTitlePl, isSubTitlePl } = this.dataModel.model || {};
        // title/ctrTitle/subTitle 设置字体颜色无关选区，整个文本框统一改色
        if (isTitleOrCtrTitlePl || isSubTitlePl) {
            // 获取当前光标或选区涉及到的段落
            const selectedParagraphs = this.editor.getNodes(node => node.type === 'paragraph');
            // 遍历其中每一段，设置这一段文字的样式
            selectedParagraphs.forEach(([paragraph, path]) => {
                this.supplyEffect(paragraph, textStyles);
                this.editor.changeTextProps(textStyles, path);
            });
        } else {
            // 获取当前光标或选区涉及到的段落
            const selectedParagraphs = this.editor.getNodes(node => node.type === 'paragraph', selection);
            // 遍历其中每一段，设置这一段文字的样式
            selectedParagraphs.forEach(([paragraph, path]) => {
                this.supplyEffect(paragraph, textStyles);
                this.editor.changeTextProps(textStyles, path);
            });
        }
    };

    /**
     * 补充已存在的效果到设置数据上
     * 母版编辑的占位符需特殊处理，因为效果属性不在paragraphList上，所以需要补充
     * @param paragraph
     * @param textStyles
     * @private
     */
    private supplyEffect(paragraph, textStyles): void {
        if (textStyles.effect) {
            const existEffect = paragraph?.children?.[0]?.effect || {};
            textStyles.effect = { ...existEffect, ...textStyles.effect };
        }
        if (textStyles['origin-effect']) {
            const existEffect = paragraph?.children?.[0]?.effect || {};
            textStyles['origin-effect'] = { ...existEffect, ...textStyles['origin-effect'] };
        }
    }

    private createOperations(): EditorOperations {
        return new EditorOperations(this.dataModel, this);
    }

    private handleSelectionChangeInEditing = (selection: Selection) => {
        const { textEditorStateService, accessibilityManager } = this.services;
        if (textEditorStateService) {
            textEditorStateService.isEditorTextExpanded = !!this.editorView.isExpanded();
        }
        if (!selection || !accessibilityManager) {
            return;
        }
        const paragraphTextAndOffset = this.getParagraphTextAndOffset(selection);
        if (!paragraphTextAndOffset) {
            return;
        }
        const { offset, focusParagraphText } = paragraphTextAndOffset;
        accessibilityManager?.promise().then((accessibilityManager) => {
            accessibilityManager?.readKeyArrowContent(offset, focusParagraphText);
        });
    };

    private handleUndo = () => {
        this.services.undoRedoService?.undo();
    };

    private handleRedo = () => {
        this.services.undoRedoService?.redo();
    };

    private onMouseUp = () => {
        this.props.onMouseUp?.();
        this.updateTextEditorState(false);
    };

    private onBlur = () => {
        this.updateTextEditorState(false);
    };

    private onMouseDown = (event: MouseEvent) => {
        this.props.onMouseDown?.(event);  // 如果查看态和编辑态都会响应mouseDown，建议props回调方到lite里
        this.updateTextEditorState(true);
    };

    private onMouseMove = () => {
        const { textEditorStateService } = this.services;
        if (textEditorStateService) {
            textEditorStateService.isEditorTextMouseDown = true;
        }
    };

    private updateTextEditorState(isMouseDown: boolean) {
        const { textEditorStateService } = this.services;
        if (textEditorStateService) {
            textEditorStateService.isEditorTextMouseDown = isMouseDown;
            textEditorStateService.isEditorTextExpanded = !!this.editorView.isExpanded();
        }
    }

    private isPreview = () => !!this.services.textEditorStateService?.isTextPreview;

    private onKeyDown = (e: KeyboardEvent) => {
        const originKeyCode = e.keyCode;
        const { accessibilityManager } = this.services;
        if (!accessibilityManager) {
            return;
        }
        const keyCode = convertKeyCodeWithTextDirection(originKeyCode, this.dataModel.textDirection);
        accessibilityManager?.promise().then((accessibilityManager) => {
            accessibilityManager.handleKeydown(keyCode);
        });
    };
}
